import { neighbors, random, adjacent, slide, reject } from './53-game-primitives.mjs';

export const PICTURE_PUZZLE_VERSION = 'picture-puzzles-1.0.0';
export const PICTURE_PUZZLE_SIZES = Object.freeze(
  [
    ['3x3', 3, 3, '3×3', 6],
    ['4x4', 4, 4, '4×4', 12],
    ['5x5', 5, 5, '5×5', 18],
    ['6x6', 6, 6, '6×6', 24],
    ['4x6', 4, 6, '4×6 portrait', 18],
    ['5x8', 5, 8, '5×8 portrait', 28],
  ].map(([id, columns, rows, label, mixing]) =>
    Object.freeze({ id, columns, rows, label, mixing }),
  ),
);
const UINT32 = 0xffffffff;
const LIMIT = 1_000_000;
const HISTORY = 50;
const solved = (board) => board.every((value, index) => value === (index + 1) % board.length);
const counter = (value) => Number.isSafeInteger(value) && value >= 0 && value <= LIMIT;
const seedValue = (value) => Number.isInteger(value) && value >= 0 && value <= UINT32;

/** Resolve a closed portrait/square layout; unknown or malformed IDs return null. */
export function getPictureSize(layout) {
  return PICTURE_PUZZLE_SIZES.find((size) => size.id === layout) || null;
}
function requireSize(layout) {
  const size = getPictureSize(layout);
  if (!size) reject('Choose a supported picture size.');
  return size;
}
function permutation(board, length) {
  return (
    Array.isArray(board) &&
    board.length === length &&
    new Set(board).size === length &&
    board.every((value) => Number.isInteger(value) && value >= 0 && value < length)
  );
}
function singleSlide(before, after, columns) {
  const blank = before.indexOf(0),
    from = after.indexOf(0);
  return (
    adjacent(blank, from, columns) &&
    before[from] === after[blank] &&
    before.every((value, index) => index === blank || index === from || value === after[index])
  );
}
function validate(state) {
  const size = requireSize(state?.layout),
    length = size.columns * size.rows;
  const fields = [
    'version',
    'layout',
    'seed',
    'rng',
    'columns',
    'rows',
    'board',
    'moves',
    'level',
    'scramble',
    'completed',
    'history',
  ];
  const mixing = size.mixing * Math.min(state.level, 3);
  if (
    Object.keys(state).some((key) => !fields.includes(key)) ||
    state.version !== PICTURE_PUZZLE_VERSION ||
    state.columns !== size.columns ||
    state.rows !== size.rows ||
    !seedValue(state.seed) ||
    !seedValue(state.rng) ||
    !counter(state.moves) ||
    !counter(state.level) ||
    state.level < 1 ||
    !counter(state.scramble) ||
    (state.scramble !== mixing && state.scramble !== mixing + 1) ||
    !permutation(state.board, length) ||
    state.completed !== solved(state.board) ||
    !Array.isArray(state.history) ||
    state.history.length > HISTORY
  )
    reject('Malformed private picture puzzle.');
  for (let index = 0; index < state.history.length; index++) {
    const previous = state.history[index],
      next = state.history[index + 1] || state;
    if (
      !previous ||
      Object.keys(previous).some((key) => !['board', 'moves'].includes(key)) ||
      !permutation(previous.board, length) ||
      !counter(previous.moves) ||
      !permutation(next.board, length) ||
      !counter(next.moves) ||
      next.moves !== previous.moves + 1 ||
      !singleSlide(previous.board, next.board, size.columns)
    )
      reject('Malformed private puzzle history.');
  }
  return size;
}

/** Test/generator witness comes only from actual legal blank moves. It is never
 * included in playable state or view. No parity repair or arbitrary tile swaps.
 * Seed and bounded mixing level determine all draws; a solved walk gets one
 * extra legal move and a matching inverse, preserving reachability. */
export function createPictureScramble(layout, seed, level = 1) {
  const size = requireSize(layout);
  if (!seedValue(seed) || !counter(level) || level < 1)
    reject('Invalid private puzzle seed or level.');
  const length = size.columns * size.rows;
  const board = Array.from({ length }, (_, index) => (index + 1) % length);
  const generator = { rng: seed };
  const steps = size.mixing * Math.min(level, 3),
    inverse = [];
  let previous = -1;
  for (let step = 0; step < steps; step++) {
    const blank = board.indexOf(0);
    const choices = neighbors(blank, size.columns, length).filter((index) => index !== previous);
    const next = choices[random(generator, choices.length)];
    [board[blank], board[next]] = [board[next], board[blank]];
    inverse.push(blank);
    previous = blank;
  }
  if (solved(board)) {
    const blank = board.indexOf(0);
    const next = neighbors(blank, size.columns, length)[0];
    [board[blank], board[next]] = [board[next], board[blank]];
    inverse.push(blank);
  }
  return { board, rng: generator.rng, scramble: inverse.length, solution: inverse.reverse() };
}
function create(layout, seed, level) {
  const size = requireSize(layout),
    mixed = createPictureScramble(layout, seed, level);
  return {
    version: PICTURE_PUZZLE_VERSION,
    layout,
    seed,
    rng: mixed.rng,
    columns: size.columns,
    rows: size.rows,
    board: mixed.board,
    moves: 0,
    level,
    scramble: mixed.scramble,
    completed: false,
    history: [],
  };
}
/** Create unscored, device-private play. Numeric seed is uint32; unknown layouts
 * reject. Default ranked3x3 policy and historical score storage are unrelated. */
export function createPicturePuzzle(layout, seed) {
  return create(layout, seed, 1);
}

/** Project only visible numbered tiles, actual correct placements and legal
 * neighbors. No solution, photo, score, session or persistent preferences. */
export function picturePuzzleView(state) {
  validate(state);
  const blank = state.board.indexOf(0);
  const current = state.board.filter((value, index) => value !== 0 && value === index + 1).length;
  return {
    version: PICTURE_PUZZLE_VERSION,
    layout: state.layout,
    columns: state.columns,
    rows: state.rows,
    moves: state.moves,
    level: state.level,
    completed: state.completed,
    canUndo: state.history.length > 0,
    progress: { current, total: state.board.length - 1, label: 'Tiles in place' },
    cells: state.board.map((value, index) => ({
      index,
      value,
      label: `${value ? `Tile ${value}` : 'Empty space'} · row ${Math.floor(index / state.columns) + 1}, column ${(index % state.columns) + 1}`,
      disabled: state.completed || value === 0 || !adjacent(index, blank, state.columns),
      move: { type: 'cell', index },
    })),
  };
}
/** Commit exactly one adjacent tile/blank swap into a fresh state. Invalid,
 * wrapped/diagonal or completed moves reject without modifying prior state.
 * Bounded Undo records actual snapshots; one-million counter prevents unsafe
 * imported state, not a ranked timer or ordinary600-action termination. */
export function applyPictureMove(state, index) {
  validate(state);
  if (
    state.completed ||
    state.moves >= LIMIT ||
    !Number.isInteger(index) ||
    index < 0 ||
    index >= state.board.length
  )
    reject('Choose a tile beside the empty space.');
  const next = structuredClone(state);
  next.completed = slide(next.board, index, next.columns);
  next.history.push({ board: [...state.board], moves: state.moves });
  next.history = next.history.slice(-HISTORY);
  next.moves++;
  return next;
}
/** Restore one genuine previous private move, including a just-solved board.
 * An empty history returns the same immutable state; it never resets a puzzle. */
export function undoPicturePuzzle(state) {
  validate(state);
  if (!state.history.length) return state;
  const next = structuredClone(state),
    previous = next.history.pop();
  next.board = previous.board;
  next.moves = previous.moves;
  next.completed = solved(next.board);
  return next;
}
/** Explicit completion-only advancement. Same layout, incremented private level
 * and deterministic fresh uint32 seed; no automatic advancement or score write. */
export function nextPicturePuzzle(state) {
  validate(state);
  if (!state.completed || state.level >= LIMIT)
    reject('Finish this picture before starting the next.');
  let seed = (Math.imul(state.rng, 1664525) + 1013904223) >>> 0;
  if (seed === state.seed) seed = (seed + 1) >>> 0;
  return create(state.layout, seed, state.level + 1);
}
